import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import type { Phase, WalkthroughState, WalkthroughConfig } from "./types";

const WalkthroughContext = createContext<WalkthroughState | null>(null);
const WalkthroughConfigContext = createContext<WalkthroughConfig | null>(null);

export function WalkthroughProvider({
  children,
  config,
}: {
  children: ReactNode;
  config: WalkthroughConfig;
}) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [introStep, setIntroStep] = useState(0);
  const [tourStep, setTourStep] = useState(0);

  // Auto-start on first login
  useEffect(() => {
    if (!localStorage.getItem(config.storageKey)) {
      setPhase("intro");
    }
  }, [config.storageKey]);

  // Lock body scroll when walkthrough is active
  useEffect(() => {
    if (phase !== "idle") {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [phase]);

  const markDone = useCallback(() => {
    localStorage.setItem(config.storageKey, "true");
    setPhase("idle");
  }, [config.storageKey]);

  const startWalkthrough = useCallback(() => {
    setIntroStep(0);
    setTourStep(0);
    setPhase("intro");
  }, []);

  const nextIntro = useCallback(() => setIntroStep((s) => s + 1), []);
  const prevIntro = useCallback(() => setIntroStep((s) => Math.max(0, s - 1)), []);
  const skipIntro = useCallback(() => markDone(), [markDone]);
  const finishIntro = useCallback(() => {
    setTourStep(0);
    setPhase("tour");
  }, []);

  const nextTour = useCallback(() => setTourStep((s) => s + 1), []);
  const prevTour = useCallback(() => setTourStep((s) => Math.max(0, s - 1)), []);
  const skipTour = useCallback(() => markDone(), [markDone]);
  const finishTour = useCallback(() => markDone(), [markDone]);

  return (
    <WalkthroughConfigContext.Provider value={config}>
      <WalkthroughContext.Provider
        value={{
          phase,
          introStep,
          tourStep,
          startWalkthrough,
          nextIntro,
          prevIntro,
          skipIntro,
          finishIntro,
          nextTour,
          prevTour,
          skipTour,
          finishTour,
        }}
      >
        {children}
      </WalkthroughContext.Provider>
    </WalkthroughConfigContext.Provider>
  );
}

export function useWalkthrough() {
  const ctx = useContext(WalkthroughContext);
  if (!ctx) throw new Error("useWalkthrough must be used within WalkthroughProvider");
  return ctx;
}

export function useWalkthroughConfig() {
  const ctx = useContext(WalkthroughConfigContext);
  if (!ctx) throw new Error("useWalkthroughConfig must be used within WalkthroughProvider");
  return ctx;
}
